<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4>{{ $t('systems.change_rc_password') }}</h4>
        </dialog-header>

        <form name="changePasswordForm" novalidate @submit.prevent="submit()" class="form">
            <div class="ngdialog__body">
                <p>
                    <a
                        class="button button--primary button--fluid"
                        @click.prevent="showCurrentPassword()"
                        v-if="!currentPassword"
                    >
                        {{ $t('systems.rc_password_dialog.show_current_rc_password') }}
                    </a>
                    <span v-if="currentPassword">
                        {{
                            $t('systems.rc_password_dialog.current_password', {
                                password: currentPassword,
                            })
                        }}
                    </span>
                </p>

                <div v-if="currentPassword && currentPassword !== '123'">
                    <p>
                        <a
                            class="button button--primary button--fluid"
                            @click.prevent="resetPassword()"
                        >
                            {{ $t('systems.rc_password_dialog.reset_to_default') }}
                        </a>
                    </p>

                    <p>
                        {{ $t('systems.rc_password_dialog.for_rc_factory_reset') }}
                    </p>
                </div>

                <div class="row form__row">
                    <div class="column-mobile-24">
                        <label for="password" class="form__label">
                            {{ $t('systems.rc_password_dialog.new_rc_password') }}
                        </label>
                        <input
                            type="text"
                            class="form__field"
                            name="password"
                            v-model="$v.formData.password.$model"
                            :placeholder="$t('fields.password')"
                            v-autofocus
                            required
                            minlength="10"
                            maxlength="32"
                            @change="clearRemoteError('password')"
                            :class="{
                                'is-invalid': $v.formData.password.$invalid,
                                'is-dirty': $v.formData.password.$dirty,
                            }"
                        />

                        <div v-if="$v.formData.password.$anyError">
                            <p class="form__error" v-if="!$v.formData.password.required">
                                {{ $t('systems.rc_password_dialog.new_rc_password_e_required') }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.password.minLength">
                                {{ $t('systems.rc_password_dialog.new_rc_password_e_minlength') }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.password.maxLength">
                                {{ $t('systems.rc_password_dialog.new_rc_password_e_maxlength') }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.password.remote">
                                {{ remoteErrors.password[0] }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.password.noSpecificExtras">
                                {{ $t('useraccount.passworderror.notallowedextras') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <dialog-buttons :submit-text="$t('systems.change_rc_password')" :disabled="loading" />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import remote from '@/validators/remote';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';
import autofocus from '@/directives/autofocus';

const noSpecificExtras = value => !/[äöüÄÖÜß]/.test(value);

export default {
    name: 'system-change-password',
    components: {
        DialogHeader,
        DialogButtons,
    },
    directives: {
        autofocus,
    },
    props: {
        system: {
            type: Object,
            required: true,
        },
    },
    data() {
        let data = {
            formData: {
                password: '',
            },
            currentPassword: '',
            remoteErrors: null,
            loading: false,
        };

        return data;
    },
    validations() {
        return {
            formData: {
                password: {
                    required,
                    minLength: minLength(10),
                    maxLength: maxLength(32),
                    remote: remote(this.remoteErrors, 'password'),
                    noSpecificExtras,
                },
            },
        };
    },
    methods: {
        showCurrentPassword() {
            api.systems.password({ id: this.system.id }).then(
                response => {
                    this.currentPassword = response.data.password;
                },
                () => {
                    this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                }
            );
        },
        resetPassword() {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('rc_password_dialog.confirm_reset_default'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('registration.reset_password'),
                    successCallback: () => {
                        api.systems.resetPassword({ id: this.system.id }).then(
                            () => {
                                this.currentPassword = '123';
                            },
                            () => {
                                this.$notify({
                                    type: 'error',
                                    text: this.$t('errors.error_occurred'),
                                });
                            }
                        );
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        submit() {
            this.remoteErrors = null;

            if (this.$v.formData.$invalid) {
                this.$v.formData.$touch();
                return;
            }

            this.loading = true;

            api.systems
                .updatePassword({ id: this.system.id, password: this.formData.password })
                .then(
                    () => {
                        this.$emit('close');
                    },
                    error => {
                        if (error.response.status === 422) {
                            this.remoteErrors = error.response.data.errors;
                        } else {
                            this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                        }
                    }
                )
                .catch()
                .then(() => {
                    this.loading = false;
                });
        },
        clearRemoteError(key) {
            if (this.remoteErrors && this.remoteErrors[key]) {
                this.remoteErrors[key] = null;
            }
        },
    },
};
</script>
